/* ===========================================================================
   reveal.js — GSAP scroll entrance animations, driven entirely by classes.

   Install:
     assets/reveal.js, then in layout/theme.liquid before </body>:
     <script src="{{ 'reveal.js' | asset_url }}" defer></script>

   Use:
     <section class="reveal">                    fades up as one block
     <section class="reveal reveal--scale">      pick a different style
     <div class="reveal-stagger">                children animate one by one
     <div class="reveal" data-delay="0.3">       tweak per element

   GSAP + ScrollTrigger load from a CDN, once, and only if the page has
   something to animate. Respects prefers-reduced-motion.
   ========================================================================= */

(function () {
  if (window.__revealBooted) return;
  window.__revealBooted = true;

  // Speed pass 2026-11-02 (A4): same GSAP 3.12.5 files, self-hosted next to
  // this script (assets/gsap.min.js, assets/ScrollTrigger.min.js), so no
  // extra connection to jsDelivr. Falls back to jsDelivr if the path is unknown.
  var SELF = (document.currentScript && document.currentScript.src) || '';
  var BASE = /gsap-js-animation\.js/.test(SELF) ? SELF.replace(/gsap-js-animation\.js.*$/, '') : '';
  var GSAP_URL = BASE ? BASE + 'gsap.min.js' : 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  var ST_URL = BASE ? BASE + 'ScrollTrigger.min.js' : 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';

  var SELECTOR = '.reveal, .reveal-stagger';
  var loader = null;

  // --- defaults, override per element with data attributes -----------------
  var DEFAULTS = {
    duration: 0.85,
    delay: 0,
    stagger: 0.08,
    distance: 28,
    start: 85,   // % down the viewport
    ease: 'power2.out'
  };

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function loadGsap() {
    if (window.gsap && window.ScrollTrigger) return Promise.resolve();
    if (loader) return loader;

    loader = (window.gsap ? Promise.resolve() : loadScript(GSAP_URL))
      .then(function () {
        return window.ScrollTrigger ? null : loadScript(ST_URL);
      })
      .then(function () {
        if (window.gsap && window.ScrollTrigger) {
          window.gsap.registerPlugin(window.ScrollTrigger);
        }
      });

    return loader;
  }

  function styleOf(el) {
    var list = el.classList;
    if (list.contains('reveal--fade')) return 'fade';
    if (list.contains('reveal--down')) return 'down';
    if (list.contains('reveal--left')) return 'left';
    if (list.contains('reveal--right')) return 'right';
    if (list.contains('reveal--scale')) return 'scale';
    if (list.contains('reveal--wipe')) return 'wipe';
    return 'up';
  }

  function fromVars(style, d) {
    switch (style) {
      case 'fade': return { opacity: 0 };
      case 'down': return { opacity: 0, y: -d };
      case 'left': return { opacity: 0, x: -d };
      case 'right': return { opacity: 0, x: d };
      case 'scale': return { opacity: 0, scale: 0.94 };
      case 'wipe': return { opacity: 0, y: d, clipPath: 'inset(0 0 100% 0)' };
      default: return { opacity: 0, y: d };
    }
  }

  function num(el, attr, fallback) {
    var v = parseFloat(el.getAttribute('data-' + attr));
    return isNaN(v) ? fallback : v;
  }

  function setup(el) {
    if (el.dataset.revealReady === 'true') return;
    el.dataset.revealReady = 'true';

    var gsap = window.gsap;
    var isGroup = el.classList.contains('reveal-stagger');

    // A stagger group animates its own children; anything marked .reveal
    // inside it, or failing that every direct child.
    var targets = el;
    if (isGroup) {
      var marked = el.querySelectorAll(':scope .reveal');
      targets = marked.length
        ? Array.prototype.slice.call(marked)
        : Array.prototype.slice.call(el.children);
      targets.forEach(function (t) { t.dataset.revealReady = 'true'; });
    }

    if (!targets || (targets.length === 0)) return;

    var style = styleOf(el);
    var distance = num(el, 'distance', DEFAULTS.distance);

    gsap.set(targets, fromVars(style, distance));

    var vars = {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      duration: num(el, 'duration', DEFAULTS.duration),
      delay: num(el, 'delay', DEFAULTS.delay),
      ease: el.getAttribute('data-ease') || DEFAULTS.ease,
      overwrite: 'auto',
      clearProps: 'transform,clipPath'
    };

    if (style === 'wipe') vars.clipPath = 'inset(0 0 0% 0)';
    if (isGroup) vars.stagger = num(el, 'stagger', DEFAULTS.stagger);

    var repeat = el.hasAttribute('data-repeat');

    vars.scrollTrigger = {
      trigger: el,
      start: 'top ' + num(el, 'start', DEFAULTS.start) + '%',
      once: !repeat
    };

    if (repeat) vars.scrollTrigger.toggleActions = 'play none none reverse';

    gsap.to(targets, vars);
  }

  function init(scope) {
    var els = (scope || document).querySelectorAll(SELECTOR);
    if (!els.length) return;

    // Anyone asking for less motion gets the content as-is, no GSAP loaded.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach(function (el) { el.dataset.revealReady = 'true'; });
      return;
    }

    loadGsap()
      .then(function () {
        if (!window.gsap) return;
        // Skip anything already claimed by a parent stagger group.
        els.forEach(function (el) {
          if (el.dataset.revealReady === 'true') return;
          if (el.classList.contains('reveal') && el.closest('.reveal-stagger')) return;
          setup(el);
        });
      })
      .catch(function () {
        /* CDN unreachable — nothing was hidden, so the page reads normally. */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor re-renders a section: clear the flags and run again.
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll(SELECTOR).forEach(function (el) {
      el.dataset.revealReady = '';
    });
    init(event.target);
  });
})();